// Pending calendar-change proposals from the AI Assistant.
// Persisted in localStorage so the user can leave and come back.

const KEY = "chronicles.proposals.pending.v1";

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(items) {
  localStorage.setItem(KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent("chronicles:proposals-changed"));
}

export function getPendingProposals() {
  return readAll();
}

export function addProposalBatch(batch) {
  // batch = { id, createdAt, summary, proposals: [...] }
  const next = [batch, ...readAll()];
  writeAll(next);
}

export function removeProposalBatch(batchId) {
  writeAll(readAll().filter((b) => b.id !== batchId));
}

export function updateProposalBatch(batchId, updater) {
  writeAll(
    readAll().map((b) => (b.id === batchId ? updater(b) : b))
  );
}

// Extracts a single ```chronicles-proposals ... ``` JSON block from assistant text.
// Returns { visibleText, proposals[] | null }.
const FENCE_RE = /```chronicles-proposals\s*([\s\S]*?)```/;
const FENCE_OPEN_RE = /```chronicles-proposals\s*/;

// Tolerant cleanup so minor model deviations (comments, trailing commas, smart
// quotes) still parse instead of silently dropping the whole proposal block.
function sanitizeJson(s) {
  return s
    .replace(/\/\*[\s\S]*?\*\//g, "") // /* block comments */
    .replace(/(^|[^:])\/\/[^\n\r]*/g, "$1") // // line comments (keep http://)
    .replace(/[\u201C\u201D]/g, '"') // smart double quotes
    .replace(/[\u2018\u2019]/g, "'") // smart single quotes
    .replace(/,\s*([}\]])/g, "$1"); // trailing commas
}

function tryParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    try {
      return JSON.parse(sanitizeJson(raw));
    } catch {
      return null;
    }
  }
}

// Salvage every COMPLETE proposal object from a (possibly truncated / unclosed)
// proposals block. Large plans occasionally get cut off mid-JSON by the model /
// stream, so a whole-block JSON.parse fails. We scan the "proposals" array with a
// brace/string-aware walker and keep each fully-closed { ... } object; a partial
// trailing object is skipped. The recovered ops feed the existing completeness /
// continuation loop, which then fills whatever is missing.
function salvageProposalObjects(block) {
  const pi = block.indexOf('"proposals"');
  let start = pi === -1 ? block.indexOf("[") : block.indexOf("[", pi);
  if (start === -1) return [];
  const out = [];
  let depth = 0, inStr = false, esc = false, objStart = -1;
  for (let i = start + 1; i < block.length; i++) {
    const c = block[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === "\\") esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') { inStr = true; continue; }
    if (c === "{") { if (depth === 0) objStart = i; depth += 1; }
    else if (c === "}") {
      depth -= 1;
      if (depth === 0 && objStart !== -1) {
        const obj = tryParse(block.slice(objStart, i + 1));
        if (obj && typeof obj.kind === "string") out.push(obj);
        objStart = -1;
      }
    } else if (c === "]" && depth === 0) {
      break; // end of the proposals array
    }
  }
  return out;
}

export function extractProposals(rawText) {
  const match = rawText.match(FENCE_RE);
  let block = null;
  let closed = true;
  if (match) {
    block = match[1];
  } else {
    // Truncated stream: a fence START with no closing ``` (large plans cut off
    // mid-JSON). Salvage what completed so continuation can finish the rest.
    const openIdx = rawText.search(FENCE_OPEN_RE);
    if (openIdx === -1) return { visibleText: rawText, proposals: null, expected: null };
    block = rawText.slice(openIdx).replace(FENCE_OPEN_RE, "");
    closed = false;
  }
  let proposals = null;
  let expected = null;
  const parsed = tryParse(block.trim());
  if (parsed && Array.isArray(parsed.proposals) && parsed.proposals.length) {
    // Action-type agnostic: keep every proposal that has a recognized kind,
    // regardless of whether it is create / edit / delete / move / etc.
    proposals = parsed.proposals.filter((p) => p && typeof p.kind === "string");
    if (proposals.length === 0) proposals = null;
  } else {
    // Whole-block parse failed (truncated / malformed) → salvage complete objects.
    const salvaged = salvageProposalObjects(block);
    if (salvaged.length) proposals = salvaged;
  }
  // Optional completeness manifest: the total number of operations the AI
  // intends for the WHOLE plan. Used to detect (and continue) under-generation
  // on large plans before Proposal Review is opened. Accepts a number or
  // { total } object. Absent → completeness check is a no-op (legacy behavior).
  if (parsed) {
    const e = parsed.expected;
    if (typeof e === "number" && e > 0) expected = e;
    else if (e && typeof e === "object" && typeof e.total === "number" && e.total > 0) expected = e.total;
  }
  if (expected == null) {
    // Regex fallback works even when the JSON was truncated before the array.
    const em = block.match(/"expected"\s*:\s*(\d+)/);
    if (em) { const n = parseInt(em[1], 10); if (n > 0) expected = n; }
  }
  const visibleText = closed
    ? rawText.replace(FENCE_RE, "").trim()
    : rawText.slice(0, rawText.search(FENCE_OPEN_RE)).trim();
  return { visibleText, proposals, expected };
}

// Extracts a single ```chronicles-memory ... ``` block of planning-memory
// suggestions (add). Returns { visibleText, suggestions[] | null }.
const MEMORY_FENCE_RE = /```chronicles-memory\s*([\s\S]*?)```/;
const VALID_MEMORY_CATS = ["framework", "goals", "projects", "preferences"];

export function extractMemorySuggestions(rawText) {
  const match = rawText.match(MEMORY_FENCE_RE);
  if (!match) return { visibleText: rawText, suggestions: null };
  let suggestions = null;
  const parsed = tryParse(match[1].trim());
  if (parsed && Array.isArray(parsed.suggestions) && parsed.suggestions.length) {
    suggestions = parsed.suggestions
      .filter((s) => s && typeof s.text === "string" && s.text.trim())
      .map((s) => ({
        action: "add",
        category: VALID_MEMORY_CATS.includes(s.category) ? s.category : "framework",
        text: s.text.trim(),
      }));
    if (suggestions.length === 0) suggestions = null;
  }
  const visibleText = rawText.replace(MEMORY_FENCE_RE, "").trim();
  return { visibleText, suggestions };
}

// Live display cleanup: while streaming, a fenced ```chronicles-* block may
// arrive token-by-token (still unclosed), so the completed-fence regexes above
// won't match yet. Cut everything from the first code fence onward so the user
// never sees raw JSON / proposal payloads mid-stream. The natural-language
// explanation always precedes the block, so nothing user-facing is lost.
export function stripStreamingArtifacts(rawText) {
  if (!rawText) return "";
  const idx = rawText.indexOf("```");
  return (idx === -1 ? rawText : rawText.slice(0, idx)).trimEnd();
}

// Final safety net for the CHAT bubble: even if a proposal block was truncated
// or malformed (so the extractors above couldn't strip a closed fence), never
// let raw proposal/JSON/internal-command text reach the user. Removes any code
// fences (closed or dangling) and any leftover proposal-payload lines.
// True if the assistant's prose CLAIMS the changes are done/ready/approvable.
// Used to catch false-success messages (en/fi/sv) when no proposal package was
// actually produced, so the app can show an honest message instead.
const SUCCESS_CLAIM_RE = new RegExp(
  [
    // English
    "ready (for|to)\\s*(your\\s*)?(review|approv)",
    "you can (now )?(approve|review)",
    "(i'?ve|i have|i)\\s*(prepared|created|added|scheduled|set up|made)\\b.*\\b(change|event|proposal|calendar)",
    "(proposals?|changes?)\\s+(are|is)\\s+ready",
    "prepared\\s+\\d+\\s+.*(change|event|proposal)",
    // Finnish
    "valmiina\\s+(tarkistettavaksi|hyv\\u00e4ksytt\\u00e4v\\u00e4ksi)",
    "voit\\s+(hyv\\u00e4ksy\\u00e4|tarkistaa)",
    "(olen\\s+valmistellut|valmistelin|olen\\s+luonut|loin)\\b",
    "(ehdotukset|muutokset)\\s+ovat\\s+valmiina",
    // Swedish
    "redo\\s+(att\\s+granska|f\\u00f6r\\s+godk\\u00e4nnande)",
    "du\\s+kan\\s+godk\\u00e4nna",
    "(jag\\s+har\\s+f\\u00f6rberett|f\\u00f6rslagen\\s+\\u00e4r\\s+redo)",
  ].join("|"),
  "i"
);

export function claimsProposalSuccess(text) {
  if (!text) return false;
  return SUCCESS_CLAIM_RE.test(text);
}

export function scrubProposalArtifacts(text) {
  if (!text) return "";
  let out = text;
  // Remove any complete fenced code blocks.
  out = out.replace(/```[\s\S]*?```/g, "");
  // Remove a dangling (unclosed) fence and everything after it.
  const idx = out.indexOf("```");
  if (idx !== -1) out = out.slice(0, idx);
  // Drop obvious internal-command / payload fragments if any slipped through.
  out = out
    .replace(/chronicles-(proposals|bulk|memory)/gi, "")
    .replace(/^\s*\{[\s\S]*"(proposals|bulk|suggestions|recurrences)"[\s\S]*$/m, "");
  return out.trim();
}
